// STRATUL STRUCTURII pentru macheta unificată (`viewer/macheta`).
//
// Același desen ca stratul «Realist» al Studioului (`Scene3D.tsx`): betonul
// translucid pe familii, cu muchiile lor lipite, ochiurile de placă, pereții
// de beton și de zidărie, fundația cu porțiunea de stâlp care coboară în ea.
// Codul fiecărei familii e cel din `Scene3D`, luat ca atare — inclusiv
// ordinea de desenare (`asaza`) și treptele de cedare la adâncime egală —,
// fiindcă «identic cu Studioul» înseamnă aceleași geometrii, aceleași
// materiale, aceeași ordine. Ce NU e aici: luminile, ceața, fundalul și
// mediul (sunt ale gazdei, macheta de situație), secțiunea, cotele, nodurile
// FEM și armătura (vin ca straturi proprii, mai târziu).
//
// Grupul e construit în CADRUL MODELULUI, cu transformare identitate: muchiile
// (`muchiiFamilie`) se coc cu `matrixWorld` al grupului, deci el trebuie să
// fie identitate la construcție; gazda îl așază apoi în desen prin
// `position` + `rotation.z` (`viewer/macheta/cadru.ts`), și geometria, și
// muchiile deopotrivă.

import * as THREE from "three";
import * as BGU from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { FemPanou, FemResult } from "../tipuri";
import { scaraImplicita, treptele } from "../scari3d";
import { axaGrinzii } from "../axaGrinzii";
import { amprentaFaraPereti, amprentaStalp, inPoligon } from "../amprente3d";
import { PAL_LAYERE_3D, PAL_TEREN } from "../paletaDesen";
import { aruncaUmbra, materialRealist, muchiiFamilie } from "../realist3d";
import { grupTerenSapatura } from "../teren3d";
import { construiesteArmatura, type ArmaturaRezumat, type GrupArmatura } from "../armatura3d";
import { CULORI, fundareaDesenata, placaPeSolDesenata, radiereDesenate } from "./model";
import type { Selectie } from "./model";

/** Familiile stratului — aceleași chei ca `vis` din `Scene3D`, ca textele
 *  comutatoarelor (`struct.fem.lay.*`) să fie aceleași. */
export type FamilieStructura =
  | "stalpi" | "grinzi" | "placa" | "pereti" | "ferestre" | "usi" | "parapete" | "nuclee"
  | "peretiBeton" | "zidarieConfinata" | "balcoane" | "placaSol" | "fundatie"
  // numai PE NIVEL (timelapse-ul): cofrajele verticalelor și ale plăcii, popii
  // de sub placă, banda nivelului cu interiorul în lucru
  | "cofrajVertical" | "cofrajPlaca" | "popi" | "aprins";

/** Vizibilitatea implicită: a Studioului (`visImplicit`), ca prima vedere
 *  să fie cea de acolo. */
export const FAMILII_IMPLICITE: Record<FamilieStructura, boolean> = {
  stalpi: true, grinzi: true, placa: true, pereti: true, ferestre: false, usi: false,
  parapete: true, nuclee: true, peretiBeton: true, zidarieConfinata: true, balcoane: false,
  placaSol: true, fundatie: true,
  cofrajVertical: false, cofrajPlaca: false, popi: false, aprins: false,
};

export interface OptiuniStrat {
  /** PE NIVEL: fiecare familie își ține piesele în subgrupuri pe corp (tronson)
   *  × etaj, cu muchiile lor, ca o stare pe săptămână să le aprindă pe rând;
   *  se construiesc și cofrajele, popii și banda nivelului aprins. */
  perNivel?: boolean;
  /** LAYERUL Studioului: «realist» (betonul translucid cu muchii, umbre) sau
   *  «clasic» (fiecare familie cu culoarea ei din paletă). */
  layer?: "realist" | "clasic";
  /** Gazda n-are `scene.environmentIntensity` al Studioului (0,38): scara
   *  intră în `envMapIntensity` al materialelor (macheta de situație). */
  mediuInMateriale?: boolean;
}
/** Ordinea de desenare a familiilor și treptele de cedare la adâncime egală:
 *  v. «cine câștigă întâlnirea a două fețe» în `Scene3D`. */
export const ORD = { stalp: 1, grinda: 2, pereteBeton: 3, fundatie: 4,
                     placa: 5, mesh: 6, invelis: 7, balcon: 8, parapet: 9, gol: 10,
                     zidarieConfinata: 11 } as const;
export const CEDEAZA = { grinda: 1, pereteBeton: 2, fundatie: 3, invelis: 4,
                         zidarieConfinata: 5 } as const;
/** Ce se vede dintr-o familie pe un corp și un etaj (starea pe săptămână);
 *  «armatura» e stratul armăturii (`armatura3d`), pe cota barelor. */
export type StareStructura = (corp: string, etaj: number, fam: FamilieStructura | "armatura") => boolean;

export interface StratStructura {
  /** cadrul modelului, identitate; gazda îl așază în desen */
  grup: THREE.Group;
  familii: Record<FamilieStructura, THREE.Group>;
  /** piesele clicabile, cu `userData.tip` / `ref` ca în Studio */
  pickables: THREE.Mesh[];
  /** câte piese are fiecare familie (pentru etichetele comutatoarelor) */
  numar: { ferestre: number; usi: number; parapete: number; nuclee: number };
  /** ce e obiectul lovit, sau `null` dacă nu e o piesă clicabilă */
  tinta: (o: THREE.Object3D) => Selectie;
  /** aprinde piesa aleasă cu aurul selecției și o stinge pe cea dinainte */
  evidentiaza: (s: Selectie) => void;
  /** PE NIVEL: aprinde subgrupurile după stare; `null` = toate (familia decide) */
  aplicaStare: (f: StareStructura | null) => void;
  /** etajele modelului, cu cotele lor (indexul = `etaj` al pieselor) */
  etaje: { etaj: number; nivel: string; z0: number; z1: number }[];
  /** TERENUL ȘI SĂPĂTURA Studioului (`teren3d`, din `model.sapatura`), construite
   *  la prima cerere și aprinse; `null` când modelul n-are săpătură. */
  terenSapatura: (aprins: boolean) => THREE.Group | null;
  /** ARMĂTURA (`armatura3d`, din rezumatul serverului), construită la cerere;
   *  `null` o scoate. Vine doar pe un corp singur, nu pe complex. */
  armatura: (rez: ArmaturaRezumat | null) => GrupArmatura | null;
  /** materialele pe familie (Studioul le pune la loc după selecție) */
  materiale: {
    matStalp: THREE.Material; matGrinda: THREE.Material; matPlaca: THREE.Material; matPereti: THREE.Material;
    matFereastra: THREE.Material; matUsa: THREE.Material; matParapet: THREE.Material; matPereteBeton: THREE.Material;
    matZc: THREE.Material; matFundatie: THREE.Material; matSel: THREE.Material; matPanouSel: THREE.Material;
  };
  /** fundația desenată (grinzile de fundare și secțiunea ei), pentru armătură */
  fundatia: { grinzi: ReturnType<typeof fundareaDesenata>["grinzi"]; fnd: ReturnType<typeof fundareaDesenata>["fnd"] };
  /** planul grilei: cota de fundare cea mai de jos */
  zTeren: number;
  dispose: () => void;
}

export function construiesteStructura(r: FemResult, opt: OptiuniStrat = {}): StratStructura {
  const model = r.model;
  const nd = new Map(model.noduri.map((n) => [n.id, n]));
  const grup = new THREE.Group();
  grup.name = "structura";
  const perNivel = !!opt.perNivel;
  const realist = (opt.layer ?? "realist") === "realist";
  const hImplicit = r.model.inaltime_etaj_m ?? 3;
  const etaje = model.etaje.map((e, k) => ({ etaj: k, nivel: e.nivel, z0: e.z_m - (e.inaltime_m ?? hImplicit), z1: e.z_m }));
  const etajLa = (niv: number) => model.etaje.findIndex((e) => e.nivel_diafragma === niv);

  // --- SUBGRUPURILE pe corp × etaj -----------------------------------------
  // Pe nivel, o piesă intră în subgrupul corpului și etajului ei; o piesă fără
  // corp scris (un ochi de placă, un gol, un nucleu) ia corpul celui mai
  // apropiat stâlp de pe etajul ei — pe complex piesele poartă tronsonul.
  const stalpiPeEtaj = new Map<number, { x: number; y: number; corp: string }[]>();
  for (const st of model.stalpi) {
    const a = nd.get(st.nod_jos); if (!a || !st.corp) continue;
    (stalpiPeEtaj.get(st.etaj) ?? stalpiPeEtaj.set(st.etaj, []).get(st.etaj)!).push({ x: a.x_m, y: a.y_m, corp: st.corp });
  }
  const corpLa = (x: number, y: number, etaj: number): string => {
    const lista = stalpiPeEtaj.get(etaj) ?? [...stalpiPeEtaj.values()].flat();
    let best = "?", bd = Infinity;
    for (const c of lista) { const d = (c.x - x) ** 2 + (c.y - y) ** 2; if (d < bd) { bd = d; best = c.corp; } }
    return best;
  };
  const centrul = (pts: [number, number][]): [number, number] =>
    [pts.reduce((a, q) => a + q[0], 0) / pts.length, pts.reduce((a, q) => a + q[1], 0) / pts.length];
  const subgrupuri = new Map<THREE.Group, Map<string, THREE.Group>>();
  /** unde intră o piesă: subgrupul (corp, etaj) al familiei, sau familia */
  const in_ = (fam: THREE.Group, corp: string | undefined, etaj: number | undefined): THREE.Object3D => {
    if (!perNivel || corp == null || etaj == null) return fam;
    const m = subgrupuri.get(fam) ?? subgrupuri.set(fam, new Map()).get(fam)!;
    const k = `${corp}|${etaj}`;
    let g = m.get(k);
    if (!g) { g = new THREE.Group(); g.userData = { corp, etaj }; fam.add(g); m.set(k, g); }
    return g;
  };

  // --- cine câștigă întâlnirea a două fețe: ca în `Scene3D` ---------------
  const cedeaza = <T extends THREE.Material>(m: T, treapta: number): T => {
    m.polygonOffset = true;
    m.polygonOffsetFactor = treapta;
    m.polygonOffsetUnits = treapta;
    return m;
  };
  const invelis = <T extends THREE.Material>(m: T, treapta: number = CEDEAZA.invelis): T => {
    cedeaza(m, treapta);
    m.depthWrite = false;
    return m;
  };
  const asaza = (g: THREE.Object3D, ordine: number) => {
    let k = 0;
    g.traverse((o) => { o.renderOrder = ordine * 100000 + k++; });
  };

  // --- elementele structurale (mesh-uri clicabile) -------------------------
  const pickables: THREE.Mesh[] = [];
  const matStalp = realist
    ? invelis(materialRealist("stalp"), 0)
    : new THREE.MeshLambertMaterial({ color: CULORI.stalp });
  const matGrinda = realist
    ? invelis(materialRealist("grinda"), CEDEAZA.grinda)
    : cedeaza(new THREE.MeshLambertMaterial({ color: CULORI.grinda }), CEDEAZA.grinda);
  const matSel = cedeaza(new THREE.MeshLambertMaterial({
    color: CULORI.selectat,
    emissive: 0x6b4a10,
  }), -1);

  const gStalpi = new THREE.Group();
  const gGrinzi = new THREE.Group();
  grup.add(gStalpi);
  grup.add(gGrinzi);
  // COFRAJELE, POPII, NIVELUL APRINS: familii ale timelapse-ului, numai pe nivel.
  // Placajul e opac și cald, ca pe șantier; popii, oțel vopsit.
  const gCofrajVertical = new THREE.Group();
  const gCofrajPlaca = new THREE.Group();
  const gPopi = new THREE.Group();
  const gAprins = new THREE.Group();
  const matCofraj = new THREE.MeshStandardMaterial({ color: 0xc9a86a, roughness: 0.85 });
  const matPopi = new THREE.MeshStandardMaterial({ color: 0x8d8d8d, roughness: 0.5, metalness: 0.4 });
  const matAprins = new THREE.MeshStandardMaterial({ color: 0xffd27a, emissive: 0xff9f1a, emissiveIntensity: 0.55, roughness: 0.6,
                                                     transparent: true, opacity: 0.45, depthWrite: false });

  model.stalpi.forEach((s, i) => {
    const a = nd.get(s.nod_jos)!;
    const b = nd.get(s.nod_sus)!;
    const L = b.z_m - a.z_m;
    const extruda = (amp: [number, number][]) => new THREE.ExtrudeGeometry(
      new THREE.Shape(amp.map(([x, y]) => new THREE.Vector2(x - a.x_m, y - a.y_m))),
      { depth: L, bevelEnabled: false });
    const taiata = amprentaFaraPereti(s, amprentaStalp(s, a), model.pereti_beton ?? []);
    let mesh: THREE.Mesh;
    let geoCote: THREE.BufferGeometry | undefined;
    if (taiata) {
      mesh = new THREE.Mesh(extruda(taiata), matStalp);
      mesh.position.set(a.x_m, a.y_m, a.z_m);
      geoCote = extruda(amprentaStalp(s, a));
    } else if (s.amprenta_m && s.amprenta_m.length >= 3) {
      mesh = new THREE.Mesh(extruda(s.amprenta_m), matStalp);
      mesh.position.set(a.x_m, a.y_m, a.z_m);
    } else {
      const geo = new THREE.BoxGeometry(s.h_m, s.b_m, L);
      mesh = new THREE.Mesh(geo, matStalp);
      mesh.position.set(a.x_m, a.y_m, a.z_m + L / 2);
      if (s.dir) mesh.rotation.z = Math.atan2(s.dir[1], s.dir[0]);
    }
    mesh.userData = {
      tip: "stalp", ref: i,
      unghi: s.dir ? Math.atan2(s.dir[1], s.dir[0]) : 0,
      ...(geoCote ? { geoCote } : {}),
    };
    in_(gStalpi, s.corp, s.etaj).add(mesh);
    pickables.push(mesh);
    if (perNivel) {
      // COFRAJUL stâlpului: cutia lui, cu 8 cm mai mare, pe cutia amprentei
      const amp = taiata ?? (s.amprenta_m && s.amprenta_m.length >= 3 ? s.amprenta_m : null);
      let cof: THREE.Mesh;
      if (amp) {
        const xs = amp.map((q) => q[0]), ys = amp.map((q) => q[1]);
        const w = Math.max(...xs) - Math.min(...xs), d = Math.max(...ys) - Math.min(...ys);
        cof = new THREE.Mesh(new THREE.BoxGeometry(w + 0.08, d + 0.08, L), matCofraj);
        cof.position.set((Math.max(...xs) + Math.min(...xs)) / 2, (Math.max(...ys) + Math.min(...ys)) / 2, a.z_m + L / 2);
      } else {
        cof = new THREE.Mesh(new THREE.BoxGeometry(s.h_m + 0.08, s.b_m + 0.08, L), matCofraj);
        cof.position.set(a.x_m, a.y_m, a.z_m + L / 2);
        if (s.dir) cof.rotation.z = Math.atan2(s.dir[1], s.dir[0]);
      }
      cof.castShadow = true;
      in_(gCofrajVertical, s.corp, s.etaj).add(cof);
    }
  });

  const matBrat = new THREE.LineDashedMaterial({
    color: CULORI.grinda, dashSize: 0.2, gapSize: 0.12,
    depthTest: false, transparent: true, opacity: 0.9,
  });
  model.grinzi.forEach((g, i) => {
    const a = nd.get(g.nod_i)!;
    const b = nd.get(g.nod_j)!;
    const axa = axaGrinzii(g, a, b);
    const [ax, ay] = axa.a;
    const [bx, by] = axa.b;
    const { L } = axa;
    if (L < 1e-6) return;
    const hInima = g.h_web_m ?? Math.max(g.h_m - g.hf_m, 0.05);
    const geo = new THREE.BoxGeometry(L, g.bw_m, hInima);
    const mesh = new THREE.Mesh(geo, matGrinda);
    const zAxa = a.z_m - g.hf_m / 2 - hInima / 2;
    mesh.position.set((ax + bx) / 2, (ay + by) / 2, zAxa);
    mesh.rotation.z = axa.unghi;
    mesh.userData = { tip: "grinda", ref: i, unghi: axa.unghi };
    in_(gGrinzi, g.corp, g.etaj).add(mesh);
    pickables.push(mesh);
    axa.brate.forEach((br) => {
      const linie = new THREE.Line(
        new THREE.BufferGeometry().setFromPoints([
          new THREE.Vector3(br.nod[0], br.nod[1], zAxa),
          new THREE.Vector3(br.cap[0], br.cap[1], zAxa),
        ]), matBrat);
      linie.computeLineDistances();
      linie.userData = { brat: true, ref: i };
      in_(gGrinzi, g.corp, g.etaj).add(linie);
    });
  });

  // --- placa (diafragma), pe ochiuri, cu grosime ----------------------------
  const gPlaca = new THREE.Group();
  const matPlaca = realist
    ? invelis(materialRealist("placa"))
    : invelis(new THREE.MeshBasicMaterial({ color: CULORI.placa, transparent: true, opacity: 0.13, side: THREE.FrontSide }));
  const matContur = new THREE.LineBasicMaterial({ color: CULORI.placa });
  const matPanouSel = invelis(new THREE.MeshBasicMaterial({
    color: CULORI.selectat,
    transparent: true,
    opacity: 0.78,
    side: THREE.FrontSide,
  }), -1);
  const tPl = model.grosime_placa_m ?? 0.15;
  const zNivel = (niv: number) =>
    model.etaje.find((e) => e.nivel_diafragma === niv)?.z_m ?? 0;
  const formaOchi = (p: FemPanou) => {
    const shape = new THREE.Shape(p.contur.map(([x, y]) => new THREE.Vector2(x, y)));
    [
      ...(p.inele ?? []).map((inel) => inel.contur),
      ...(p.zone_gol ?? []).map((z) => z.contur),
    ]
      .filter((g): g is [number, number][] => (g?.length ?? 0) >= 3)
      .forEach((g) => {
        shape.holes.push(new THREE.Path(g.map(([x, y]) => new THREE.Vector2(x, y))));
      });
    return shape;
  };
  const panouri = model.panouri ?? [];
  if (panouri.length) {
    panouri.forEach((p, i) => {
      if (!p.contur || p.contur.length < 3) return;
      const zSus = zNivel(p.nivel) + tPl / 2;
      const mesh = new THREE.Mesh(
        new THREE.ExtrudeGeometry(formaOchi(p), { depth: tPl, bevelEnabled: false }),
        matPlaca,
      );
      mesh.position.z = zSus - tPl;
      mesh.userData = { tip: "panou", ref: i };
      const etajP = etajLa(p.nivel); const [cx, cy] = centrul(p.contur);
      const corpP = perNivel ? corpLa(cx, cy, etajP) : undefined;
      in_(gPlaca, corpP, etajP).add(mesh);
      pickables.push(mesh);
      const pts = [...p.contur, p.contur[0]].map(([x, y]) => new THREE.Vector3(x, y, zSus));
      in_(gPlaca, corpP, etajP).add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(pts), matContur));
      if (perNivel && etajP >= 0) cofrajulPlacii(formaOchi(p), p.contur, corpP!, etajP, zSus - tPl);
    });
  }
  /** COFRAJUL PLĂCII (o tablă sub placă), POPII (la 1,5 m, de la pardoseala
   *  de sub ea) și BANDA NIVELULUI APRINS (interiorul în lucru), pe conturul
   *  unei plăci a etajului `etaj` cu fața de jos la `zJos`. */
  const cofrajulPlacii = (shape: THREE.Shape, inel: [number, number][], corp: string, etaj: number, zJos: number) => {
    const cof = new THREE.Mesh(new THREE.ExtrudeGeometry(shape, { depth: 0.04, bevelEnabled: false }), matCofraj);
    cof.position.z = zJos - 0.04;
    in_(gCofrajPlaca, corp, etaj).add(cof);
    const zPard = etaje[etaj]?.z0 ?? (zJos - hImplicit);
    const h = zJos - 0.04 - zPard;
    if (h > 0.5) {
      const xs = inel.map((q) => q[0]), ys = inel.map((q) => q[1]);
      const popi: THREE.BufferGeometry[] = [];
      const PAS = 1.5;
      for (let x = Math.min(...xs) + PAS / 2; x < Math.max(...xs); x += PAS) {
        for (let y = Math.min(...ys) + PAS / 2; y < Math.max(...ys); y += PAS) {
          if (!inPoligon(x, y, inel)) continue;
          const c = new THREE.CylinderGeometry(0.04, 0.04, h, 6);
          c.rotateX(Math.PI / 2); c.translate(x, y, zPard + h / 2);
          popi.push(c);
        }
      }
      if (popi.length) {
        const g = BGU.mergeGeometries(popi, false);
        popi.forEach((q) => q.dispose());
        if (g) in_(gPopi, corp, etaj).add(new THREE.Mesh(g, matPopi));
      }
      const banda = new THREE.Mesh(new THREE.ExtrudeGeometry(shape, { depth: Math.max(h - 0.6, 0.3), bevelEnabled: false }), matAprins);
      banda.position.z = zPard + 0.3;
      in_(gAprins, corp, etaj).add(banda);
    }
  };
  model.etaje.forEach((et, k) => {
    const zSus = et.z_m + tPl / 2;
    et.slab.forEach((rings) => {
      const [cx, cy] = centrul(rings[0]);
      const corpR = perNivel ? corpLa(cx, cy, k) : undefined;
      if (!panouri.length) {
        const shape = new THREE.Shape(rings[0].map(([x, y]) => new THREE.Vector2(x, y)));
        rings.slice(1).forEach((hole) => {
          shape.holes.push(new THREE.Path(hole.map(([x, y]) => new THREE.Vector2(x, y))));
        });
        const mesh = new THREE.Mesh(
          new THREE.ExtrudeGeometry(shape, { depth: tPl, bevelEnabled: false }),
          matPlaca,
        );
        mesh.position.z = zSus - tPl;
        in_(gPlaca, corpR, k).add(mesh);
        if (perNivel) cofrajulPlacii(shape, rings[0], corpR!, k, zSus - tPl);
      }
      rings.forEach((ring) => {
        const pts = ring.map(([x, y]) => new THREE.Vector3(x, y, zSus));
        in_(gPlaca, corpR, k).add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(pts), matContur));
      });
    });
  });
  asaza(gPlaca, ORD.placa);
  grup.add(gPlaca);

  // --- pereții de ZIDĂRIE — clicabili ---------------------------------------
  const gPereti = new THREE.Group();
  const matPereti = realist
    ? invelis(materialRealist("zid"))
    : invelis(new THREE.MeshLambertMaterial({ color: CULORI.zidarie, opacity: 0.42, transparent: true, side: THREE.FrontSide }));
  let iZid = 0;
  model.etaje.forEach((et, kEtaj) => {
    const hEtaj = et.inaltime_m ?? r.model.inaltime_etaj_m ?? 3;
    (et.pereti ?? []).forEach((w) => {
      const inele = w.inele ?? [];
      if (inele.length === 0 || inele[0].length < 3) { iZid++; return; }
      const forma = (rings: [number, number][][]) => {
        const shape = new THREE.Shape(rings[0].map(([x, y]) => new THREE.Vector2(x, y)));
        rings.slice(1).forEach((hole) => {
          shape.holes.push(new THREE.Path(hole.map(([x, y]) => new THREE.Vector2(x, y))));
        });
        return shape;
      };
      let unghiZid = 0;
      let ceaMaiLunga = 0;
      inele[0].forEach(([ax, ay], k) => {
        const [bx, by] = inele[0][(k + 1) % inele[0].length];
        const l = Math.hypot(bx - ax, by - ay);
        if (l > ceaMaiLunga) {
          ceaMaiLunga = l;
          unghiZid = Math.atan2(by - ay, bx - ax);
        }
      });
      const ref = iZid++;
      const piese = w.piese ?? [];
      const bucati: { inele: [number, number][][]; z0: number; z1: number }[] =
        piese.length
          ? piese.map((p) => ({ inele: p.inele, z0: p.z0_m, z1: p.z1_m }))
          : [{ inele, z0: 0, z1: hEtaj }];
      bucati.forEach((b) => {
        if (!b.inele.length || b.inele[0].length < 3 || b.z1 - b.z0 <= 0) return;
        const geo = new THREE.ExtrudeGeometry(forma(b.inele), { depth: b.z1 - b.z0, bevelEnabled: false });
        const mesh = new THREE.Mesh(geo, matPereti);
        mesh.position.z = et.z_m - hEtaj + b.z0;
        mesh.userData = { tip: "perete_zidarie", ref, unghi: unghiZid };
        in_(gPereti, w.corp ?? (perNivel ? corpLa(...centrul(inele[0]), kEtaj) : undefined), kEtaj).add(mesh);
        pickables.push(mesh);
      });
    });
  });
  asaza(gPereti, ORD.invelis);
  grup.add(gPereti);

  // --- GOLURILE: ferestrele și ușile importate — clicabile -----------------
  const GOL_NECOTAT_M = 0.05;
  const gFerestre = new THREE.Group();
  const gUsi = new THREE.Group();
  const matFereastra = invelis(new THREE.MeshLambertMaterial({
    color: PAL_LAYERE_3D.ferestre,
    transparent: true, opacity: 0.5, side: THREE.FrontSide,
  }));
  const matUsa = invelis(new THREE.MeshLambertMaterial({
    color: PAL_LAYERE_3D.usi,
    transparent: true, opacity: 0.5, side: THREE.FrontSide,
  }));
  let iFer = 0;
  let iUsa = 0;
  model.etaje.forEach((et, kEtaj) => {
    const hEtaj = et.inaltime_m ?? r.model.inaltime_etaj_m ?? 3;
    const zPardoseala = et.z_m - hEtaj;
    (et.goluri ?? []).forEach((g) => {
      const usa = g.tip === "usa";
      const ref = usa ? iUsa++ : iFer++;
      if ((g.colturi ?? []).length < 3) return;
      const shape = new THREE.Shape(g.colturi.map(([x, y]) => new THREE.Vector2(x, y)));
      const cotat = g.hp_m != null && g.h_m != null;
      const depth = cotat ? g.h_m! : GOL_NECOTAT_M;
      const geo = new THREE.ExtrudeGeometry(shape, { depth, bevelEnabled: false });
      const mesh = new THREE.Mesh(geo, usa ? matUsa : matFereastra);
      mesh.position.z = zPardoseala + (cotat ? g.hp_m! : 0);
      mesh.userData = { tip: usa ? "usa" : "fereastra", ref };
      in_(usa ? gUsi : gFerestre, perNivel ? corpLa(...centrul(g.colturi), kEtaj) : undefined, kEtaj).add(mesh);
      pickables.push(mesh);
    });
  });
  asaza(gFerestre, ORD.gol);
  asaza(gUsi, ORD.gol);
  grup.add(gFerestre);
  grup.add(gUsi);

  // --- PARAPETELE — clicabile ------------------------------------------------
  const gParapete = new THREE.Group();
  const matParapet = invelis(new THREE.MeshLambertMaterial({
    color: PAL_LAYERE_3D.margine,
    transparent: true, opacity: 0.75, side: THREE.FrontSide,
  }));
  let iPar = 0;
  model.etaje.forEach((et, kEtaj) => {
    const hEtaj = et.inaltime_m ?? r.model.inaltime_etaj_m ?? 3;
    (et.margini ?? []).forEach((m) => {
      const inele = m.inele ?? [];
      if (inele.length === 0 || inele[0].length < 3) { iPar++; return; }
      const shape = new THREE.Shape(inele[0].map(([x, y]) => new THREE.Vector2(x, y)));
      inele.slice(1).forEach((hole) => {
        shape.holes.push(new THREE.Path(hole.map(([x, y]) => new THREE.Vector2(x, y))));
      });
      const geo = new THREE.ExtrudeGeometry(shape, { depth: m.h_m ?? GOL_NECOTAT_M, bevelEnabled: false });
      const mesh = new THREE.Mesh(geo, matParapet);
      mesh.position.z = et.z_m - hEtaj;
      mesh.userData = { tip: "parapet", ref: iPar++ };
      in_(gParapete, perNivel ? corpLa(...centrul(inele[0]), kEtaj) : undefined, kEtaj).add(mesh);
      pickables.push(mesh);
    });
  });
  asaza(gParapete, ORD.parapet);
  grup.add(gParapete);

  // --- NUCLEUL: casele de scară și lifturile — doar afișare ----------------
  const gNuclee = new THREE.Group();
  let nNuclee = 0;
  const matScara = invelis(new THREE.MeshLambertMaterial({
    color: CULORI.scara, transparent: true, opacity: 0.35, side: THREE.FrontSide,
  }));
  const matLift = invelis(new THREE.MeshLambertMaterial({
    color: CULORI.lift, transparent: true, opacity: 0.35, side: THREE.FrontSide,
  }));
  const matTreapta = new THREE.MeshLambertMaterial({ color: CULORI.scara });
  model.etaje.forEach((et, kEtaj) => {
    const hEtaj = et.inaltime_m ?? r.model.inaltime_etaj_m ?? 3;
    (et.nuclee ?? []).forEach((nc) => {
      const inele = nc.inele ?? [];
      if (inele.length === 0 || inele[0].length < 3) return;
      nNuclee++;
      const shape = new THREE.Shape(inele[0].map(([x, y]) => new THREE.Vector2(x, y)));
      inele.slice(1).forEach((hole) => {
        shape.holes.push(new THREE.Path(hole.map(([x, y]) => new THREE.Vector2(x, y))));
      });
      const geo = new THREE.ExtrudeGeometry(shape, { depth: hEtaj, bevelEnabled: false });
      const mesh = new THREE.Mesh(geo, nc.kind === "lift" ? matLift : matScara);
      mesh.position.z = et.z_m - hEtaj;
      mesh.userData = { tip: "nucleu", kind: nc.kind, id: nc.id };
      const gN = in_(gNuclee, perNivel ? corpLa(...centrul(inele[0]), kEtaj) : undefined, kEtaj);
      gN.add(mesh);
      if (nc.kind !== "scara") return;
      const scara = nc.scara?.rampe?.length ? nc.scara : scaraImplicita(inele[0], hEtaj);
      if (!scara) return;
      const { trepte, podeste } = treptele(scara, et.z_m - hEtaj, hEtaj);
      trepte.forEach((tr) => {
        const m = new THREE.Mesh(new THREE.BoxGeometry(tr.lung, tr.lat, tr.h), matTreapta);
        m.position.set(tr.cx, tr.cy, tr.z0 + tr.h / 2);
        m.rotation.z = tr.unghi;
        m.userData = { tip: "nucleu", kind: "scara", id: nc.id };
        gN.add(m);
      });
      podeste.forEach((pd) => {
        const m = new THREE.Mesh(new THREE.BoxGeometry(pd.lung, pd.lat, pd.grosime), matTreapta);
        m.position.set(pd.cx, pd.cy, pd.z - pd.grosime / 2);
        m.rotation.z = pd.unghi;
        m.userData = { tip: "nucleu", kind: "scara", id: nc.id };
        gN.add(m);
      });
    });
  });
  asaza(gNuclee, ORD.invelis);
  grup.add(gNuclee);

  // --- pereții de BETON — clicabili -----------------------------------------
  const gPeretiBeton = new THREE.Group();
  const matPereteBeton = realist
    ? invelis(materialRealist("pereteBeton"), CEDEAZA.pereteBeton)
    : cedeaza(new THREE.MeshLambertMaterial({ color: CULORI.pereteBeton }), CEDEAZA.pereteBeton);
  (model.pereti_beton ?? []).forEach((p, i) => {
    const [ax0, ay0] = p.a_xy;
    const [bx0, by0] = p.b_xy;
    const dx = bx0 - ax0;
    const dy = by0 - ay0;
    const L = Math.hypot(dx, dy);
    if (L < 1e-6) return;
    const hPb = p.h_desen_m ?? p.h_web_m;
    let zJosPb = p.z_jos_m ?? (
      p.z_sus_m - (p.e_offset_m ?? p.hf_m / 2 + p.h_web_m / 2) - p.h_web_m / 2);
    // MODELUL COMPLEXULUI (`complex_corpuri.py`) scrie panourile fără cotele
    // lor (`z_sus_m`, `z_jos_m`, `hf_m` lipsesc; în Studio ele ies la NaN,
    // adică nu se văd): panoul stă pe pardoseala etajului lui, cât e de înalt
    if (!Number.isFinite(zJosPb)) {
      const et = model.etaje[p.etaj] ?? model.etaje.find((e) => e.nivel === p.nivel);
      zJosPb = et ? et.z_m - (et.inaltime_m ?? r.model.inaltime_etaj_m ?? 3) : 0;
    }
    if (!Number.isFinite(hPb) || !Number.isFinite(zJosPb)) return;
    const geo = new THREE.BoxGeometry(L, p.t_m, hPb);
    const mesh = new THREE.Mesh(geo, matPereteBeton);
    mesh.position.set((ax0 + bx0) / 2, (ay0 + by0) / 2, zJosPb + hPb / 2);
    mesh.rotation.z = Math.atan2(dy, dx);
    mesh.userData = { tip: "perete_beton", ref: i, unghi: Math.atan2(dy, dx) };
    in_(gPeretiBeton, p.corp, p.etaj).add(mesh);
    pickables.push(mesh);
    if (perNivel) {
      const cof = new THREE.Mesh(new THREE.BoxGeometry(L, p.t_m + 0.08, hPb), matCofraj);
      cof.position.copy(mesh.position); cof.rotation.z = mesh.rotation.z; cof.castShadow = true;
      in_(gCofrajVertical, p.corp, p.etaj).add(cof);
    }
  });
  asaza(gPeretiBeton, ORD.pereteBeton);
  grup.add(gPeretiBeton);

  // --- ZIDĂRIA CONFINATĂ și stâlpișorii ei — clicabilă -----------------------
  const gZidarieConfinata = new THREE.Group();
  const matZc = realist
    ? invelis(materialRealist("zid"), CEDEAZA.zidarieConfinata)
    : cedeaza(new THREE.MeshLambertMaterial({ color: CULORI.zidarieConfinata }), CEDEAZA.zidarieConfinata);
  const matStalpisor = realist
    ? invelis(materialRealist("stalp"), CEDEAZA.zidarieConfinata)
    : new THREE.MeshLambertMaterial({ color: CULORI.stalpisorZc });
  (model.pereti_zidarie ?? []).forEach((p, i) => {
    const [ax0, ay0] = p.a_xy;
    const [bx0, by0] = p.b_xy;
    const dx = bx0 - ax0;
    const dy = by0 - ay0;
    const L = Math.hypot(dx, dy);
    if (L < 1e-6) return;
    const hZc = p.h_web_m;
    const zJosZc = p.z_sus_m - (p.e_offset_m ?? p.hf_m / 2 + p.h_web_m / 2) - p.h_web_m / 2;
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(L, p.t_m, hZc), matZc);
    mesh.position.set((ax0 + bx0) / 2, (ay0 + by0) / 2, zJosZc + hZc / 2);
    mesh.rotation.z = Math.atan2(dy, dx);
    mesh.userData = { tip: "zidarie_confinata", ref: i, unghi: Math.atan2(dy, dx) };
    in_(gZidarieConfinata, p.corp, p.etaj).add(mesh);
    pickables.push(mesh);
  });
  (model.stalpisori_zc ?? []).forEach((st) => {
    const h = st.z_sus_m - st.z_jos_m;
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(st.latura_m, st.t_m, h), matStalpisor);
    mesh.position.set(st.xy[0], st.xy[1], st.z_jos_m + h / 2);
    mesh.rotation.z = Math.atan2(st.u[1], st.u[0]);
    gZidarieConfinata.add(mesh);
  });
  asaza(gZidarieConfinata, ORD.zidarieConfinata);
  grup.add(gZidarieConfinata);

  // --- balcoanele: consolă de beton sau LOGIE --------------------------------
  const gBalcoane = new THREE.Group();
  const matBalconBeton = realist
    ? invelis(materialRealist("balcon"))
    : invelis(new THREE.MeshLambertMaterial({ color: CULORI.balconBeton, transparent: true, opacity: 0.9 }));
  const matLogie = invelis(new THREE.MeshLambertMaterial({
    color: CULORI.logie, transparent: true, opacity: 0.35,
  }));
  const matTerasa = new THREE.LineDashedMaterial({ color: CULORI.placa, dashSize: 0.25, gapSize: 0.15 });
  model.etaje.forEach((et, kEtaj) => {
    (et.balcoane ?? []).forEach((b) => {
      if (!b.contur || b.contur.length < 3) return;
      const gB = in_(gBalcoane, perNivel ? corpLa(...centrul(b.contur), kEtaj) : undefined, kEtaj);
      if (b.tip === "terasa_pe_placa") {
        const linie = new THREE.Line(
          new THREE.BufferGeometry().setFromPoints(
            [...b.contur, b.contur[0]].map(([x, y]) => new THREE.Vector3(x, y, et.z_m + tPl / 2 + 0.01))),
          matTerasa);
        linie.computeLineDistances();
        gB.add(linie);
        return;
      }
      const shape = new THREE.Shape(b.contur.map(([x, y]) => new THREE.Vector2(x, y)));
      const geo = new THREE.ExtrudeGeometry(shape, { depth: tPl, bevelEnabled: false });
      const mesh = new THREE.Mesh(geo, b.tip === "logie" ? matLogie : matBalconBeton);
      mesh.position.z = et.z_m - tPl / 2;
      gB.add(mesh);
    });
  });
  asaza(gBalcoane, ORD.balcon);
  grup.add(gBalcoane);

  // --- PLACA PE SOL: reală, dar nestructurală ------------------------------
  const gPlacaSol = new THREE.Group();
  const psol = placaPeSolDesenata(model);
  if (psol?.contur?.length) {
    const matPlacaSol = invelis(new THREE.MeshLambertMaterial({
      color: CULORI.zidarie, transparent: true, opacity: 0.55, side: THREE.FrontSide,
    }));
    const matBalast = invelis(new THREE.MeshLambertMaterial({
      color: PAL_TEREN.necoeziv.culoare, transparent: true, opacity: 0.5, side: THREE.FrontSide,
    }));
    psol.contur.forEach((inele, i) => {
      if (!inele.length || inele[0].length < 3) return;
      const shape = new THREE.Shape(inele[0].map(([x, y]) => new THREE.Vector2(x, y)));
      inele.slice(1).forEach((gaura) => {
        shape.holes.push(new THREE.Path(gaura.map(([x, y]) => new THREE.Vector2(x, y))));
      });
      const zJos = (psol.z_sus_m ?? 0) - psol.grosime_m;
      const mesh = new THREE.Mesh(
        new THREE.ExtrudeGeometry(shape, { depth: psol.grosime_m, bevelEnabled: false }), matPlacaSol);
      mesh.position.z = zJos;
      mesh.userData = { tip: "placa_pe_sol", ref: i };
      pickables.push(mesh);
      const gS = in_(gPlacaSol, psol.elemente?.[i]?.corp ?? (perNivel ? corpLa(...centrul(inele[0]), 0) : undefined), 0);
      gS.add(mesh);
      if (psol.balast_m) {
        const balast = new THREE.Mesh(
          new THREE.ExtrudeGeometry(shape, { depth: psol.balast_m, bevelEnabled: false }), matBalast);
        balast.position.z = zJos - psol.balast_m;
        gS.add(balast);
      }
    });
  }
  asaza(gPlacaSol, ORD.invelis);
  grup.add(gPlacaSol);

  // --- FUNDAȚIA: betonul turnat, porțiunea de stâlp, radierul --------------
  const gFundatie = new THREE.Group();
  const { fnd, grinzi: grinziFund } = fundareaDesenata(model);
  const radiere = radiereDesenate(model);
  const detaliuFund = fnd.detaliu ?? "T";
  const hPesteFund = fnd.h_peste_m ?? 0.3;
  const hCFund = fnd.H_c_m ?? 0.6;
  const matFundatie = realist
    ? invelis(materialRealist("fundatie"), CEDEAZA.fundatie)
    : cedeaza(new THREE.MeshLambertMaterial({ color: CULORI.fundatie }), CEDEAZA.fundatie);
  const matFundatieSimplu = invelis(new THREE.MeshLambertMaterial({
    color: CULORI.beton, transparent: true, opacity: 0.9,
  }));
  const zSusFund = -hPesteFund;
  const zJosFundBaza = -(fnd.Df_m || hPesteFund + hCFund);
  const zJosAx = (g: { ax?: string; Df_m?: number }) => {
    const d = g.Df_m ?? (g.ax ? fnd.pe_ax?.[g.ax]?.Df_m : undefined);
    return d ? -d : zJosFundBaza;
  };
  // planul grilei = cota de fundare cea mai de JOS (fața de jos a betonului);
  // pe radier, fața de jos a balastului lui
  const zTeren = grinziFund.length || radiere.length
    ? Math.min(grinziFund.length ? zJosFundBaza : 0,
               ...grinziFund.map(zJosAx),
               ...radiere.map((x) => x.z_jos_m - (x.balast_m ?? 0)))
    : -0.02;
  grinziFund.forEach((g, i) => {
    const a = nd.get(g.nod_i);
    const b = nd.get(g.nod_j);
    if (!a || !b) return;
    const dx = b.x_m - a.x_m;
    const dy = b.y_m - a.y_m;
    const L = Math.hypot(dx, dy);
    if (L < 1e-6) return;
    const pe = fnd.pe_ax?.[g.ax];
    const bAx = g.B_m || pe?.B_m || fnd.B_m || 0.6;
    const zJosFund = zJosAx(g);
    const hCAx = g.H_c_m ?? pe?.H_c_m ?? hCFund;
    const rot = Math.atan2(dy, dx);
    const cutie = (lat: number, inalt: number, zc: number, mat: THREE.Material, clicabil: boolean) => {
      if (inalt <= 1e-6 || lat <= 1e-6) return;
      const mesh = new THREE.Mesh(new THREE.BoxGeometry(L, lat, inalt), mat);
      mesh.position.set((a.x_m + b.x_m) / 2, (a.y_m + b.y_m) / 2, zc);
      mesh.rotation.z = rot;
      if (clicabil) {
        mesh.userData = { tip: "fundatie", ref: i, ax: g.ax, unghi: rot };
        pickables.push(mesh);
      }
      in_(gFundatie, perNivel ? corpLa((a.x_m + b.x_m) / 2, (a.y_m + b.y_m) / 2, 0) : undefined, 0).add(mesh);
    };
    if (detaliuFund === "T") {
      const hT = pe?.h_talpa_m ?? fnd.h_talpa_m ?? 0.3;
      const bInima = pe?.b_inima_m ?? fnd.b_inima_m ?? 0.3;
      const hInima = Math.max(zSusFund - zJosFund - hT, 0);
      cutie(bAx, hT, zJosFund + hT / 2, matFundatie, true);
      cutie(bInima, hInima, zJosFund + hT + hInima / 2, matFundatie, true);
    } else {
      cutie(bAx, hCAx, zSusFund - hCAx / 2, matFundatie, true);
      const hBloc = detaliuFund === "bloc" ? (fnd.h_bloc_m ?? 0) : 0;
      cutie(bAx, hBloc, zSusFund - hCAx - hBloc / 2, matFundatieSimplu, false);
    }
  });
  if (grinziFund.length && zSusFund < -1e-6) {
    const noduriFund = new Set<number>();
    grinziFund.forEach((g) => {
      noduriFund.add(g.nod_i);
      noduriFund.add(g.nod_j);
    });
    model.stalpi.forEach((st, i) => {
      const a = nd.get(st.nod_jos);
      if (!a || Math.abs(a.z_m) > 1e-6 || !noduriFund.has(a.id)) return;
      const amp = amprentaFaraPereti(st, amprentaStalp(st, a), model.pereti_beton ?? []) ?? amprentaStalp(st, a);
      const mesh = new THREE.Mesh(new THREE.ExtrudeGeometry(
        new THREE.Shape(amp.map(([x, y]) => new THREE.Vector2(x - a.x_m, y - a.y_m))),
        { depth: -zSusFund, bevelEnabled: false }), matStalp);
      mesh.position.set(a.x_m, a.y_m, zSusFund);
      mesh.userData = { tip: "stalp", ref: i, unghi: st.dir ? Math.atan2(st.dir[1], st.dir[0]) : 0 };
      in_(gFundatie, st.corp, 0).add(mesh);
      pickables.push(mesh);
    });
  }
  const matBalastRadier = invelis(new THREE.MeshLambertMaterial({
    color: PAL_TEREN.necoeziv.culoare, transparent: true, opacity: 0.5, side: THREE.FrontSide,
  }));
  radiere.forEach((rad, i) => {
    rad.contur.forEach((inele) => {
      if (!inele.length || inele[0].length < 3) return;
      const shape = new THREE.Shape(inele[0].map(([x, y]) => new THREE.Vector2(x, y)));
      inele.slice(1).forEach((gaura) => {
        shape.holes.push(new THREE.Path(gaura.map(([x, y]) => new THREE.Vector2(x, y))));
      });
      const mesh = new THREE.Mesh(
        new THREE.ExtrudeGeometry(shape, { depth: rad.h_m, bevelEnabled: false }), matFundatie);
      mesh.position.z = rad.z_jos_m;
      mesh.userData = { tip: "radier", ref: i };
      pickables.push(mesh);
      const gR = in_(gFundatie, rad.corp ?? (perNivel ? corpLa(...centrul(inele[0]), 0) : undefined), 0);
      gR.add(mesh);
      if (rad.balast_m) {
        const balast = new THREE.Mesh(
          new THREE.ExtrudeGeometry(shape, { depth: rad.balast_m, bevelEnabled: false }), matBalastRadier);
        balast.position.z = rad.z_jos_m - rad.balast_m;
        gR.add(balast);
      }
    });
  });
  asaza(gStalpi, ORD.stalp);
  asaza(gGrinzi, ORD.grinda);
  asaza(gFundatie, ORD.fundatie);
  grup.add(gFundatie);

  // --- ce ADAUGĂ layerul «Realist»: umbrele și muchiile familiilor ----------
  // Studioul ține mediul de reflexie la 0,38 (`scene.environmentIntensity`):
  // la intensitate plină, cerul machetei spală betonul până la alb. Aici
  // scena e a gazdei, deci scara intră în materiale.
  const MEDIU = 0.38;
  if (opt.mediuInMateriale) grup.traverse((o) => {
    const mat = (o as THREE.Mesh).material as THREE.MeshStandardMaterial | undefined;
    if (mat && (mat as THREE.MeshStandardMaterial).isMeshStandardMaterial && !mat.userData.mediuScalat) {
      mat.envMapIntensity *= MEDIU;
      mat.userData.mediuScalat = true;
    }
  });
  const deAruncat: { dispose: () => void }[] = [];
  if (realist) [gStalpi, gGrinzi, gPlaca, gBalcoane, gPeretiBeton].forEach(aruncaUmbra);
  if (realist) ([[gStalpi, "stalp", ORD.stalp], [gGrinzi, "grinda", ORD.grinda],
    [gPlaca, "placa", ORD.placa], [gBalcoane, "balcon", ORD.balcon],
    [gPeretiBeton, "pereteBeton", ORD.pereteBeton],
    [gPereti, "zid", ORD.invelis],
    [gFundatie, "fundatie", ORD.fundatie]] as const).forEach(([g, fam, ord]) => {
    // pe nivel, muchiile fiecărui subgrup, ca să se stingă odată cu el
    const tinte = perNivel ? [...(subgrupuri.get(g)?.values() ?? [])] : [g];
    for (const tg of tinte) {
      const m = muchiiFamilie(tg, fam);
      if (!m) continue;
      m.renderOrder = ord * 100000 + 99999;
      tg.add(m);
      deAruncat.push(m.geometry, m.material as THREE.Material);
    }
  });
  if (perNivel) {
    asaza(gCofrajVertical, ORD.stalp); asaza(gCofrajPlaca, ORD.placa); asaza(gPopi, ORD.grinda); asaza(gAprins, ORD.invelis);
    grup.add(gCofrajVertical, gCofrajPlaca, gPopi, gAprins);
  }

  const familii: Record<FamilieStructura, THREE.Group> = {
    stalpi: gStalpi, grinzi: gGrinzi, placa: gPlaca, pereti: gPereti, ferestre: gFerestre, usi: gUsi,
    parapete: gParapete, nuclee: gNuclee, peretiBeton: gPeretiBeton, zidarieConfinata: gZidarieConfinata,
    balcoane: gBalcoane, placaSol: gPlacaSol, fundatie: gFundatie,
    cofrajVertical: gCofrajVertical, cofrajPlaca: gCofrajPlaca, popi: gPopi, aprins: gAprins,
  };
  for (const [k, g] of Object.entries(familii)) g.visible = FAMILII_IMPLICITE[k as FamilieStructura];
  const aplicaStare = (f: StareStructura | null) => {
    for (const [k, g] of Object.entries(familii)) {
      const subs = subgrupuri.get(g);
      if (!subs) continue;
      for (const sg of subs.values()) {
        const u = sg.userData as { corp: string; etaj: number };
        sg.visible = f ? f(u.corp, u.etaj, k as FamilieStructura) : true;
      }
    }
  };

  // --- selecția: aurul paletei, ca în Studio ---------------------------------
  const matDupaTip = (tip: string): THREE.Material => {
    switch (tip) {
      case "stalp": return matStalp;
      case "grinda": return matGrinda;
      case "panou": return matPlaca;
      case "perete_zidarie": return matPereti;
      case "fereastra": return matFereastra;
      case "usa": return matUsa;
      case "parapet": return matParapet;
      case "perete_beton": return matPereteBeton;
      case "zidarie_confinata": return matZc;
      case "placa_pe_sol": return (gPlacaSol.children[0] as THREE.Mesh | undefined)?.material as THREE.Material ?? matPereti;
      default: return matFundatie;
    }
  };
  let prev: THREE.Mesh[] = [];
  const evidentiaza = (s: Selectie) => {
    prev.forEach((m) => {
      m.material = matDupaTip((m.userData as { tip: string }).tip);
      if (m.userData.ordineInainte !== undefined) {
        m.renderOrder = m.userData.ordineInainte as number;
        delete m.userData.ordineInainte;
      }
    });
    prev = [];
    if (!s) return;
    let alese: THREE.Mesh[];
    if (s.tip === "fundatie") {
      const ax = pickables.find((m) => m.userData.tip === "fundatie" && m.userData.ref === s.ref)?.userData.ax;
      alese = pickables.filter((m) => m.userData.tip === "fundatie" && m.userData.ax === ax);
    } else {
      alese = pickables.filter((m) => m.userData.tip === s.tip && m.userData.ref === s.ref);
    }
    alese.forEach((m) => {
      m.material = s.tip === "panou" ? matPanouSel : matSel;
      if (s.tip === "panou") {
        m.userData.ordineInainte = m.renderOrder;
        m.renderOrder = (Math.max(...Object.values(ORD)) + 1) * 100000;
      }
    });
    prev = alese;
  };
  const TIPURI = new Set(["grinda", "stalp", "perete_beton", "perete_zidarie", "panou", "fundatie",
                          "fereastra", "usa", "parapet", "zidarie_confinata", "placa_pe_sol", "radier"]);
  const tinta = (o: THREE.Object3D): Selectie => {
    const u = o.userData as { tip?: string; ref?: number };
    if (!u.tip || !TIPURI.has(u.tip) || typeof u.ref !== "number") return null;
    return { tip: u.tip, ref: u.ref } as NonNullable<Selectie>;
  };

  // --- terenul și săpătura, armătura: la cerere ------------------------------
  let gTeren: THREE.Group | null = null;
  const terenSapatura = (aprins: boolean): THREE.Group | null => {
    if (!model.sapatura) return null;
    if (!gTeren) {
      // cutia: nodurile și gura gropii, cu o margine, ca în Studio
      const xs: number[] = [], ys: number[] = [];
      for (const n of model.noduri) { xs.push(n.x_m); ys.push(n.y_m); }
      for (const inel of model.sapatura.groapa?.inel_sus ?? []) for (const [x, y] of inel) { xs.push(x); ys.push(y); }
      const xmin = Math.min(...xs), xmax = Math.max(...xs), ymin = Math.min(...ys), ymax = Math.max(...ys);
      const marja = Math.max(2.0, Math.max(xmax - xmin, ymax - ymin) * 0.08);
      gTeren = grupTerenSapatura(model.sapatura, { xmin: xmin - marja, xmax: xmax + marja, ymin: ymin - marja, ymax: ymax + marja });
      gTeren.name = "terenSapatura";
      grup.add(gTeren);
    }
    gTeren.visible = aprins;
    return gTeren;
  };
  let gArm: GrupArmatura | null = null;
  const armatura = (rez: ArmaturaRezumat | null): GrupArmatura | null => {
    if (gArm) { grup.remove(gArm.grup); gArm.dispose(); gArm = null; }
    if (!rez) return null;
    gArm = construiesteArmatura(r, rez, { grinzi: grinziFund, fnd });
    grup.add(gArm.grup);
    return gArm;
  };

  const dispose = () => {
    if (gArm) { gArm.dispose(); gArm = null; }
    const mats = new Set<THREE.Material>();
    grup.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.geometry) m.geometry.dispose();
      if (m.userData?.geoCote) (m.userData.geoCote as THREE.BufferGeometry).dispose();
      if (m.material) (Array.isArray(m.material) ? m.material : [m.material]).forEach((x) => mats.add(x));
    });
    mats.forEach((m) => m.dispose());
    deAruncat.forEach((d) => d.dispose());
    grup.clear();
  };

  return {
    grup, familii, pickables,
    numar: { ferestre: iFer, usi: iUsa, parapete: iPar, nuclee: nNuclee },
    tinta, evidentiaza, aplicaStare, etaje, terenSapatura, armatura, dispose, zTeren,
    materiale: { matStalp, matGrinda, matPlaca, matPereti, matFereastra, matUsa, matParapet, matPereteBeton, matZc, matFundatie, matSel, matPanouSel },
    fundatia: { grinzi: grinziFund, fnd },
  };
}
